import { Component, effect, inject, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { UserAdminService } from '../../../core/services/user-admin.service';
import { AdminUserResponse } from '../../../core/models';
import { AppDialog } from '../../../shared/ui/dialog/app-dialog';
import { generatePassword } from '../../../shared/utils/password.util';

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Particulier',    value: 'PARTICULIER' },
  { label: 'Professionnel',  value: 'PROFESSIONNEL' },
];

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule,
    PasswordModule, SelectButtonModule, TooltipModule, AppDialog],
  templateUrl: './user-form.html',
  styleUrl: './user-form.scss'
})
export class UserFormComponent {
  private svc   = inject(UserAdminService);
  private fb    = inject(FormBuilder);
  private toast = inject(MessageService);

  readonly accountTypeOptions = ACCOUNT_TYPE_OPTIONS;

  visible = model<boolean>(false);
  created = output<AdminUserResponse>();

  saving = signal(false);
  createError = signal('');

  form = this.fb.group({
    firstName:   ['', Validators.required],
    lastName:    ['', Validators.required],
    email:       ['', [Validators.required, Validators.email]],
    phone:       ['', Validators.required],
    password:    ['', [Validators.required, Validators.minLength(8)]],
    accountType: ['PARTICULIER', Validators.required],
    companyName: [''],
    nif:         ['']
  });

  constructor() {
    this.form.get('accountType')?.valueChanges.subscribe(type => {
      const companyName = this.form.get('companyName');
      if (type === 'PROFESSIONNEL') {
        companyName?.setValidators([Validators.required]);
      } else {
        companyName?.clearValidators();
        companyName?.setValue('');
        this.form.get('nif')?.setValue('');
      }
      companyName?.updateValueAndValidity();
    });

    effect(() => {
      if (this.visible()) {
        this.form.reset({ accountType: 'PARTICULIER' });
        this.createError.set('');
      }
    });
  }

  get isProfessionnel() { return this.form.get('accountType')?.value === 'PROFESSIONNEL'; }

  close() {
    this.visible.set(false);
  }

  generatePassword() {
    this.form.get('password')?.setValue(generatePassword());
  }

  submitCreate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.createError.set('');

    const v = this.form.getRawValue();
    const data = {
      firstName:   v.firstName ?? '',
      lastName:    v.lastName ?? '',
      email:       v.email ?? '',
      phone:       v.phone ?? '',
      password:    v.password ?? '',
      accountType: v.accountType ?? 'PARTICULIER',
      companyName: v.accountType === 'PROFESSIONNEL' ? (v.companyName || undefined) : undefined,
      nif:         v.accountType === 'PROFESSIONNEL' ? (v.nif || undefined) : undefined,
    };

    this.svc.createUser(data).pipe(catchError(err => {
      this.createError.set(err?.error?.message ?? "La création de l'utilisateur a échoué.");
      this.saving.set(false);
      return of(null);
    })).subscribe(res => {
      if (res) {
        this.saving.set(false);
        this.visible.set(false);
        this.toast.add({ severity: 'success', summary: 'Utilisateur créé', detail: `« ${res.firstName} ${res.lastName} » a été créé.`, life: 4000 });
        this.created.emit(res);
      } else {
        this.saving.set(false);
      }
    });
  }
}
